import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { categoriesService } from '@/services/inventory';
import type { Category } from '@/types/inventory';
import { cn } from '@/lib/utils';
import { IMAGE_MAX_BYTES, imageTooLargeMessage } from '../imageProfile';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface Props {
  categoryId: string;
  kind: 'tile' | 'banner';
  /** Current image URL (null = none). Owned by the parent form. */
  currentUrl: string | null;
  /** Called with the updated category after upload/clear. */
  onChanged: (category: Category) => void;
}

/**
 * Single category image slot — a square tile or a wide banner — with
 * preview, replace and remove. Uploads to S3 via the category endpoints;
 * the storefront reads the URLs from the category API (imageUrl / bannerUrl).
 */
export function CategoryImageField({ categoryId, kind, currentUrl, onChanged }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const isBanner = kind === 'banner';

  const upload = useMutation({
    mutationFn: (file: File) =>
      isBanner
        ? categoriesService.uploadBanner(categoryId, file)
        : categoriesService.uploadImage(categoryId, file),
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
      onChanged(cat);
      toast.success(isBanner ? 'Banner updated' : 'Tile image updated');
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; message?: string };
      if (e?.status === 413 || (typeof e?.message === 'string' && /too large|payload/i.test(e.message))) {
        toast.error(imageTooLargeMessage());
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      isBanner
        ? categoriesService.clearBanner(categoryId)
        : categoriesService.clearImage(categoryId),
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
      onChanged(cat);
      toast.success(isBanner ? 'Banner removed' : 'Tile image removed');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove'),
  });

  const handleFile = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error(`Type ${file.type || 'unknown'} not supported. Use JPEG, PNG, WebP or GIF.`);
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error(imageTooLargeMessage());
      return;
    }
    upload.mutate(file);
  };

  const busy = upload.isPending || remove.isPending;
  // Explicit height (not aspect-ratio, which can collapse to 0 inside some
  // scroll/flex contexts). Tile = small square; banner = wide strip.
  const dims = isBanner ? 'h-24 w-full' : 'h-24 w-24';

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files);
          e.target.value = '';
        }}
      />

      {currentUrl ? (
        <div className="space-y-2">
          <div className={cn('relative overflow-hidden rounded-lg border border-border/70 bg-muted', dims)}>
            <img src={currentUrl} alt={kind} className="h-full w-full object-cover" />
            {busy && (
              <div className="absolute inset-0 grid place-items-center bg-background/60">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
              <RefreshCw className="h-3.5 w-3.5" /> Replace
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              className="text-destructive hover:text-destructive"
              onClick={() => remove.mutate()}
              disabled={busy}
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files);
          }}
          className={cn(
            'flex flex-col items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors',
            dims,
            dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/50',
          )}
        >
          {upload.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <Upload className="h-5 w-5 text-muted-foreground" />
          )}
          <div className="mt-1 px-2 text-[11px] text-foreground">
            <span className="font-medium text-primary">Choose a file</span>
          </div>
          <div className="px-2 text-[10px] text-muted-foreground">
            {isBanner ? '3:1 · 1800×600' : 'square · 800×800'}
          </div>
        </button>
      )}
    </div>
  );
}
